/**
 * useSession Hook - Continuous Session Validation
 *
 * WHY THIS ARCHITECTURE:
 * Critical security component that continuously validates user sessions.
 * Uses React Query for automatic background revalidation rather than
 * manual polling because:
 * 1. Automatic retry and error handling
 * 2. Shared cache across all components
 * 3. Background refetching without UI blocking
 * 4. Automatic cleanup on unmount
 *
 * SECURITY RATIONALE:
 * - 15-second validation interval detects revoked sessions quickly
 * - Zero retries on failure (fail-fast for security)
 * - Automatic token cleanup on validation failure
 * - Prevents authenticated UI with invalid tokens
 *
 * BUSINESS LOGIC:
 * AWS Cognito tokens expire after 1 hour (access) and 30 days (refresh).
 * This hook ensures we catch:
 * - Expired tokens before user actions fail
 * - Revoked sessions (admin action, password change)
 * - Account suspensions or deletions
 * - Token tampering attempts
 *
 * @see apiAuth.verifySession - Backend validation logic
 * @see ProtectedRoute - Consumes this for route guarding
 */
import { useQuery } from "@tanstack/react-query";
import { verifySession } from "../../services/apiAuth";
import { useToken } from "../../hooks/useToken";

/**
 * Validates and monitors user session status.
 *
 * WHY THIS PATTERN:
 * - Single source of truth for session validity across the app
 * - Automatic background revalidation without manual intervention
 * - Immediate token cleanup on validation failure
 *
 * PERFORMANCE CONSIDERATIONS:
 * - 15-second interval balances security with API load
 * - Shared query cache prevents duplicate validation calls
 * - Background fetching doesn't block UI interactions
 *
 * @returns {Object} Session validation state and loading status
 */
export function useSession() {
  const { clearTokens } = useToken();

  const {
    isLoading,
    data: isValidSession,
    error,
  } = useQuery({
    queryKey: ["user-auth"],
    queryFn: verifySession,
    enabled: true,
    // WHY retry: 0 - Security-critical operations should fail fast
    // We don't retry auth validation because:
    // 1. Invalid tokens won't become valid with retries
    // 2. Reduces time user sees authenticated UI with invalid session
    // 3. Prevents token refresh loops with expired refresh tokens
    retry: 0,
    // WHY 15 seconds: Balances security with performance
    // - Quick enough to catch revoked sessions before damage
    // - Slow enough to not overload the API
    // - Matches typical user interaction patterns (page changes)
    refetchInterval: 15000, // 15 seconds
    onError: (err: any) => {
      // WHY: Immediate token cleanup on validation failure
      // Security requirement: Invalid tokens must be removed immediately
      // to prevent any authenticated actions with compromised credentials
      clearTokens();
    },
  });

  return {
    isLoading,
    isValidSession,
    error,
  };
}
